import { IconButton, Stack, Typography, Grid } from '@mui/material';

import React, { useState, useCallback, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as DrawerClose } from 'common/assets/images/drawer-close.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import {
	ATTACH_DOCUMENT_LABEL,
	UPLOAD_FILE_SIZE,
	UPLOAD_FILE_FORMAT,
	maxFileSizeAllowedForChat,
	UPLOAD_LABEL,
	CANCEL_LABEL,
} from 'common/constants/index';
import { RootState } from 'common/store/AppStore';
import { setActiveChatSessionUploadingFiles } from 'common/store/reducer/commonReducer';

import { useUploadForm } from 'hooks/useUploadForm';

import { IUploadingFile } from 'features/Reporting/api/type-def';

import ModalEle from '../Modal/ModalEle';

import FileDrop from './FileDrop';

import styles from './FileUpload.module.scss';

interface IFileUploadProps {
	fileData?: File[];
	isMultiFileUpload?: boolean;
	onCancelHandler: () => void;
	maxFileUploadLimit?: number;
	url: string;
	acceptTypes: unknown;
	onFileUploadSuccess?: () => void;
	onFileUploadFailed?: (uploadResponse: any) => void;
}
const FileUpload = (props: IFileUploadProps) => {
	const {
		onCancelHandler,
		maxFileUploadLimit = 1,
		isMultiFileUpload = false,
		url,
		acceptTypes,
		onFileUploadSuccess,
		onFileUploadFailed,
	} = props;

	const { drawerCloseIconColor } = styles;
	const modalBoxStyles = { width: '35%', height: '35%', zIndex: '1255' };
	const [fileData, setFileData] = useState<File[]>([]);
	const [uploadBtnSelected, setUploadBtnStatus] = useState<boolean>(false);
	const [isUploadSelected, setIsUploadSelected] = useState<boolean>(false);

	const dispatch = useDispatch();

	const uploadedDocumentProgress = useSelector(
		(state: RootState) => state.common.uploadedDocumentProgress,
	);
	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);

	const { uploadForm, isSuccess, multiFileUploadFailed, uploadResponse } =
		useUploadForm(url, dispatch, loggedInUser?.email);
	const onFileUploadClickHandler = useCallback(async () => {
		setUploadBtnStatus(true);
		setIsUploadSelected(true);
		const formData = new FormData();
		const fileBlobArr: IUploadingFile[] = [];
		fileData.forEach((file) => {
			const uploadingFile: IUploadingFile = {
				name: file.name,
				size: file.size,
				type: file.type,
				blobURL: URL.createObjectURL(file),
			};
			formData.append('file', file);
			fileBlobArr.push(uploadingFile);
		});
		dispatch(setActiveChatSessionUploadingFiles(fileBlobArr));
		await uploadForm(formData, true, fileData);
	}, [fileData]);
	const cancelClickHandler = () => {
		onCancelHandler();
	};

	const handleFileData = (data: File[]) => {
		setFileData(data);
	};

	const getUploadBtnStatus = () => {
		let flag = false;

		if (
			fileData.length === 0 ||
			(maxFileUploadLimit === 1 && fileData.length > 1) ||
			(maxFileUploadLimit > 1 && fileData.length > maxFileUploadLimit) ||
			uploadBtnSelected
		) {
			flag = true;
		}

		return flag;
	};
	useEffect(() => {
		if (isSuccess && onFileUploadSuccess) {
			onFileUploadSuccess();
			setIsUploadSelected(false);
		}
	}, [isSuccess]);
	useEffect(() => {
		if (multiFileUploadFailed && onFileUploadFailed) {
			onFileUploadFailed(uploadResponse);
			setIsUploadSelected(false);
		}
	}, [multiFileUploadFailed]);
	return (
		<ModalEle
			open
			handleClose={cancelClickHandler}
			sxBoxProps={modalBoxStyles}
			restrictDefaultCloseAction
		>
			<Stack
				direction="row"
				justifyContent="space-between"
				mb={2}
				data-testid="fileupload-modal"
			>
				<Typography className={styles['fileupload-attachdocument-text']}>
					{ATTACH_DOCUMENT_LABEL}
				</Typography>
				<IconButton
					size="medium"
					onClick={cancelClickHandler}
					data-testid="close-button-icon"
					className={styles['fileupload-close']}
					disableRipple
				>
					<DrawerClose fill={drawerCloseIconColor} />
				</IconButton>
			</Stack>
			<Stack direction="column">
				<Typography>{UPLOAD_FILE_SIZE}</Typography>
				<Typography>{UPLOAD_FILE_FORMAT}</Typography>
			</Stack>
			<FileDrop
				accept={acceptTypes}
				handleFileData={handleFileData}
				fileData={fileData}
				isLoading={false}
				maxfileSize={maxFileSizeAllowedForChat}
				maxFilesLimit={maxFileUploadLimit}
				isMultiFileUpload={isMultiFileUpload}
				uploadProgress={uploadedDocumentProgress}
				isUploadSelected={uploadBtnSelected}
			/>
			<Grid
				item
				xs={12}
				sm={12}
				md={12}
				xl={12}
				lg={12}
				mt={1}
				className={styles['fileupload-btn-container']}
			>
				<Stack
					direction="row"
					className={styles['fileupload-btn-stack']}
					spacing={2}
					justifyContent="end"
				>
					<ButtonComp
						clickHandler={cancelClickHandler}
						btnType="secondary"
						data-testid="cancel-btn"
						disabled={isUploadSelected}
					>
						{CANCEL_LABEL}
					</ButtonComp>
					<ButtonComp
						btnType="primary"
						clickHandler={onFileUploadClickHandler}
						disabled={getUploadBtnStatus()}
						data-testid="upload-btn"
					>
						{UPLOAD_LABEL}
					</ButtonComp>
				</Stack>
			</Grid>
		</ModalEle>
	);
};

export default FileUpload;
